import React, { type ComponentType, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Breadcrumb from '@atlassian/jira-breadcrumbs/src/breadcrumb/component.tsx';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Report as ReportType } from '../common/types';
import messages from './messages';

type Props = {
	boardId: number;
	reportsUrl: string;
} & Intl & {
		Header: ComponentType<{
			reportType: ReportType;
			boardId: number;
		}>;
		children: ReactNode;
		reportType: ReportType;
	};

export function Report({ Header, boardId, reportType, reportsUrl, children, intl }: Props) {
	return (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName={`${reportType}ReportPage`}
			containerId={`${boardId}`}
			containerType="board"
			objectId={reportType}
			objectType="report"
			attributes={{
				boardId,
			}}
		>
			<Breadcrumb
				breadcrumb={{
					text: intl.formatMessage(messages.reportsBreadcrumbTitle),
					href: reportsUrl,
					type: 'reports',
				}}
			>
				<Container>
					<Header reportType={reportType} boardId={boardId} />
					<Content>{children}</Content>
				</Container>
			</Breadcrumb>
		</ContextualAnalyticsData>
	);
}

export default injectIntl(Report);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	padding: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP}px)`,
	display: 'flex',
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div({
	flex: 1,
	overflowY: 'auto',
	padding: `0 ${token('space.400', '32px')}`,
});
